import { useState, useEffect } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gauge, Loader2 } from "lucide-react";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ token: "", question: "…" });
  const [answer, setAnswer] = useState("");
  const [hp, setHp] = useState("");

  const loadCaptcha = () => api.get("/auth/captcha").then((r) => setCaptcha(r.data)).catch(() => {});
  useEffect(() => { loadCaptcha(); }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(email, password, name, { captcha_token: captcha.token, captcha_answer: answer, hp });
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setAnswer(""); loadCaptcha();
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground"><Gauge className="h-6 w-6" /></div>
          <div>
            <div className="font-heading text-2xl font-extrabold uppercase tracking-wide">APIS</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Manager</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold uppercase">Registrácia</h1>
          <p className="mt-1 text-sm text-muted-foreground">Vytvorte si účet – začnete v demo režime s ukážkovými dátami.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Meno / názov servisu</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="min-h-[48px]" data-testid="register-name-input" placeholder="Napr. Servis Novák" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="min-h-[48px]" data-testid="register-email-input" placeholder="vas@email.sk" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Heslo</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="min-h-[48px]" data-testid="register-password-input" placeholder="••••••••" />
        </div>
        <input type="text" name="company" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
        <div className="space-y-2">
          <Label htmlFor="captcha">Kontrola proti spamu: koľko je {captcha.question}?</Label>
          <Input id="captcha" type="number" value={answer} onChange={(e) => setAnswer(e.target.value)} required className="min-h-[48px]" data-testid="register-captcha-input" placeholder="Výsledok" />
        </div>
        {error && <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</div>}
        <Button type="submit" className="min-h-[48px] w-full text-base font-semibold" disabled={loading} data-testid="register-submit-button">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Vytvoriť účet"}
        </Button>
        <p className="text-center text-sm text-muted-foreground">Máte už účet? <Link to="/login" className="text-primary hover:underline" data-testid="to-login-link">Prihlásiť sa</Link></p>
        <p className="text-center text-xs text-muted-foreground">
          <Link to="/obchodne-podmienky" className="hover:text-foreground hover:underline" data-testid="register-terms-link">Obchodné podmienky</Link>
          {" · "}
          <Link to="/ochrana-osobnych-udajov" className="hover:text-foreground hover:underline" data-testid="register-privacy-link">Ochrana osobných údajov</Link>
        </p>
      </form>
    </div>
  );
}
